import React, { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Shield,
  CheckCircle,
  XCircle,
  HelpCircle,
  FileText,
  Download
} from 'lucide-react';
import { Badge } from './shared/Badge';

// Type definitions
interface Phase1RedFlags {
  badEventQuickSwitchClean: boolean;
  rapidBackAndForth: boolean;
  ghostCarrierViolation: boolean;
  dumpAndReturnAfterIdle: boolean;
  shellCarrierOnlyBadEvents: boolean;
}

interface Phase2Indicators {
  relatedCarriers: boolean;
  sameOwner: boolean;
  sameAddress: boolean;
  samePhone: boolean;
  recentlyCreatedCarriers: boolean;
}

interface Phase3Whitelist {
  longGaps: boolean;
  legitimateSale: boolean;
  linearProgression: boolean;
  rentalUnit: boolean;
  postRevocationDispersal: boolean;
}

interface DecisionMatrix {
  condition: string;
  action: string;
}

interface FraudAnalysisData {
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reason: string;
  details: string[];
  fraudDetected: boolean;
  phase1RedFlags: Phase1RedFlags;
  phase2Indicators: Phase2Indicators;
  phase3Whitelist: Phase3Whitelist;
  decisionMatrix: DecisionMatrix;
  carrierStatuses?: Array<{
    dotNumber: number;
    carrierName: string;
    status: string;
    statusCode: string;
  }>;
}

interface FraudAnalysisBreakdownProps {
  fraudAnalysis: FraudAnalysisData;
  vin: string;
  onExport?: () => void;
}

// Tooltip component
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="inline-flex items-center cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-[var(--z-modal)] w-64 p-2 text-xs text-white bg-gray-800 rounded shadow-lg -top-2 left-full ml-2">
          {text}
          <div className="absolute top-3 -left-1 w-2 h-2 bg-gray-800 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

// Expandable section component
const ExpandableSection: React.FC<{
  title: string;
  badge?: React.ReactNode;
  defaultExpanded?: boolean;
  headerColor?: string;
  children: React.ReactNode;
}> = ({ title, badge, defaultExpanded = false, headerColor = 'bg-gray-50', children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between ${headerColor} hover:bg-opacity-80 transition-colors`}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        {badge}
      </button>
      {expanded && (
        <div className="p-4 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

// Phase 1 Red Flags tooltips
const phase1Tooltips: Record<keyof Phase1RedFlags, string> = {
  badEventQuickSwitchClean: "Vehicle receives a violation/crash, switches carriers within 60 days, then gets a clean inspection at the new carrier - classic violation laundering pattern.",
  rapidBackAndForth: "Vehicle ping-pongs between the same carriers multiple times within 18 months. This 'A -> B -> A -> B' pattern is a strong indicator of coordinated fraud.",
  ghostCarrierViolation: "Bad event occurred on a carrier that was inactive/out-of-service for >90 days. Ghost carriers are shell companies used to absorb violations.",
  dumpAndReturnAfterIdle: "Vehicle moves to a shell carrier after violation, receives bad events there, then returns to original carrier with clean record - sophisticated dump-and-return scheme.",
  shellCarrierOnlyBadEvents: "A carrier has only 1-2 events for this VIN, and ALL are bad events (100% bad). This carrier exists solely to absorb violations in a ping-pong pattern."
};

// Phase 2 Indicators tooltips
const phase2Tooltips: Record<keyof Phase2Indicators, string> = {
  relatedCarriers: "Carriers involved in the switches have detectable business relationships, suggesting coordinated activity.",
  sameOwner: "Carriers share the same owner or legal name, indicating common control.",
  sameAddress: "Carriers operate from the same physical address - a strong indicator they are related shell companies.",
  samePhone: "Carriers share the same phone number, suggesting they are operated by the same entity.",
  recentlyCreatedCarriers: "Multiple carriers involved were created within the last 18 months - newly created carriers are often used in shell game schemes."
};

// Phase 3 Whitelist tooltips
const phase3Tooltips: Record<keyof Phase3Whitelist, string> = {
  longGaps: "All carrier switches have gaps >180 days without suspicious patterns. Long gaps typically indicate legitimate sales, not fraud.",
  legitimateSale: "Single carrier transfer with 90-180 day gap and long dwell time (>365 days) with new carrier - pattern consistent with vehicle sale.",
  linearProgression: "Vehicle moves forward through carriers without returning to previous ones. This A -> B -> C pattern is typical of legitimate leasing arrangements.",
  rentalUnit: "Vehicle is identified as a rental unit. Rental vehicles legitimately move between many carriers.",
  postRevocationDispersal: "Switch occurred within 90 days after old carrier's revocation to an unrelated active carrier - legitimate fleet dispersal."
};

// Flag item component
const FlagItem: React.FC<{
  label: string;
  detected: boolean;
  tooltip: string;
  variant: 'red' | 'orange' | 'green';
}> = ({ label, detected, tooltip, variant }) => {
  const colorClasses = {
    red: detected ? 'text-red-600' : 'text-gray-400',
    orange: detected ? 'text-orange-600' : 'text-gray-400',
    green: detected ? 'text-green-600' : 'text-gray-400',
  };

  const bgClasses = {
    red: detected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200',
    orange: detected ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200',
    green: detected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200',
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${bgClasses[variant]} mb-2`}>
      <div className="flex items-center gap-2">
        {detected ? (
          variant === 'green' ? (
            <CheckCircle className={`w-5 h-5 ${colorClasses[variant]}`} />
          ) : (
            <XCircle className={`w-5 h-5 ${colorClasses[variant]}`} />
          )
        ) : (
          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
        )}
        <span className={`text-sm font-medium ${detected ? 'text-gray-900' : 'text-gray-500'}`}>
          {label}
        </span>
      </div>
      <Tooltip text={tooltip}>
        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
      </Tooltip>
    </div>
  );
};

// Risk level badge colors
const getRiskLevelStyle = (level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): {
  bgColor: string;
  textColor: string;
  borderColor: string;
} => {
  switch (level) {
    case 'CRITICAL':
      return { bgColor: 'bg-red-600', textColor: 'text-white', borderColor: 'border-red-600' };
    case 'HIGH':
      return { bgColor: 'bg-orange-500', textColor: 'text-white', borderColor: 'border-orange-500' };
    case 'MEDIUM':
      return { bgColor: 'bg-yellow-500', textColor: 'text-white', borderColor: 'border-yellow-500' };
    case 'LOW':
      return { bgColor: 'bg-green-500', textColor: 'text-white', borderColor: 'border-green-500' };
  }
};

export const FraudAnalysisBreakdown: React.FC<FraudAnalysisBreakdownProps> = ({
  fraudAnalysis,
  vin,
  onExport
}) => {
  const riskStyle = getRiskLevelStyle(fraudAnalysis.level);

  // Count active flags in each phase
  const phase1Count = Object.values(fraudAnalysis.phase1RedFlags).filter(Boolean).length;
  const phase2Count = Object.values(fraudAnalysis.phase2Indicators).filter(Boolean).length;
  const phase3Count = Object.values(fraudAnalysis.phase3Whitelist).filter(Boolean).length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Section 1: Overall Risk Summary */}
      <div className={`p-6 border-b-4 ${
        fraudAnalysis.level === 'CRITICAL' ? 'border-red-600 bg-red-50' :
        fraudAnalysis.level === 'HIGH' ? 'border-orange-500 bg-orange-50' :
        fraudAnalysis.level === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
        'border-green-500 bg-green-50'
      }`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${riskStyle.bgColor}`}>
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Advanced Fraud Analysis</h3>
              <p className="text-sm text-gray-600 mt-1">VIN: {vin}</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`inline-block px-4 py-2 rounded-lg ${riskStyle.bgColor} ${riskStyle.textColor} font-bold text-xl`}>
              {fraudAnalysis.level} RISK
            </div>
            {fraudAnalysis.fraudDetected && (
              <div className="mt-2 flex items-center gap-1 text-red-600 font-semibold justify-end">
                <AlertTriangle className="w-4 h-4" />
                <span>Fraud Detected</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-800 font-medium">{fraudAnalysis.reason}</p>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-white rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-600">{phase1Count}/5</div>
            <div className="text-xs text-gray-600">Phase 1 Red Flags</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-600">{phase2Count}/5</div>
            <div className="text-xs text-gray-600">Phase 2 Indicators</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{phase3Count}/5</div>
            <div className="text-xs text-gray-600">Phase 3 Whitelist</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Section 2: Phase 1 Red Flags */}
        <ExpandableSection
          title="Phase 1: Critical Red Flags"
          badge={
            <Badge variant={phase1Count > 0 ? 'danger' : 'success'} size="sm">
              {phase1Count} detected
            </Badge>
          }
          defaultExpanded={phase1Count > 0}
          headerColor={phase1Count > 0 ? 'bg-red-50' : 'bg-gray-50'}
        >
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-4">
              Critical patterns that indicate active fraud. Any of these flags warrants investigation.
            </p>
          </div>
          <FlagItem
            label="Bad Event -> Quick Switch -> Clean"
            detected={fraudAnalysis.phase1RedFlags.badEventQuickSwitchClean}
            tooltip={phase1Tooltips.badEventQuickSwitchClean}
            variant="red"
          />
          <FlagItem
            label="Rapid Back-and-Forth Pattern"
            detected={fraudAnalysis.phase1RedFlags.rapidBackAndForth}
            tooltip={phase1Tooltips.rapidBackAndForth}
            variant="red"
          />
          <FlagItem
            label="Ghost Carrier Violation"
            detected={fraudAnalysis.phase1RedFlags.ghostCarrierViolation}
            tooltip={phase1Tooltips.ghostCarrierViolation}
            variant="red"
          />
          <FlagItem
            label="Dump-and-Return After Idle"
            detected={fraudAnalysis.phase1RedFlags.dumpAndReturnAfterIdle}
            tooltip={phase1Tooltips.dumpAndReturnAfterIdle}
            variant="red"
          />
          <FlagItem
            label="Shell Carrier Only Bad Events"
            detected={fraudAnalysis.phase1RedFlags.shellCarrierOnlyBadEvents}
            tooltip={phase1Tooltips.shellCarrierOnlyBadEvents}
            variant="red"
          />
        </ExpandableSection>

        {/* Section 3: Phase 2 Supporting Indicators */}
        <ExpandableSection
          title="Phase 2: Supporting Evidence"
          badge={
            <Badge variant={phase2Count > 0 ? 'warning' : 'default'} size="sm">
              {phase2Count} detected
            </Badge>
          }
          defaultExpanded={phase2Count > 0}
          headerColor={phase2Count > 0 ? 'bg-orange-50' : 'bg-gray-50'}
        >
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-4">
              Carrier relationship indicators. These strengthen fraud cases when combined with Phase 1 flags.
            </p>
          </div>
          <FlagItem
            label="Related Carriers Detected"
            detected={fraudAnalysis.phase2Indicators.relatedCarriers}
            tooltip={phase2Tooltips.relatedCarriers}
            variant="orange"
          />
          <FlagItem
            label="Same Owner"
            detected={fraudAnalysis.phase2Indicators.sameOwner}
            tooltip={phase2Tooltips.sameOwner}
            variant="orange"
          />
          <FlagItem
            label="Same Address"
            detected={fraudAnalysis.phase2Indicators.sameAddress}
            tooltip={phase2Tooltips.sameAddress}
            variant="orange"
          />
          <FlagItem
            label="Same Phone Number"
            detected={fraudAnalysis.phase2Indicators.samePhone}
            tooltip={phase2Tooltips.samePhone}
            variant="orange"
          />
          <FlagItem
            label="Recently Created Carriers"
            detected={fraudAnalysis.phase2Indicators.recentlyCreatedCarriers}
            tooltip={phase2Tooltips.recentlyCreatedCarriers}
            variant="orange"
          />
        </ExpandableSection>

        {/* Section 4: Phase 3 Whitelist Patterns */}
        <ExpandableSection
          title="Phase 3: Legitimate Patterns"
          badge={
            <Badge variant={phase3Count > 0 ? 'success' : 'default'} size="sm">
              {phase3Count} detected
            </Badge>
          }
          defaultExpanded={phase3Count > 0}
          headerColor={phase3Count > 0 ? 'bg-green-50' : 'bg-gray-50'}
        >
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-4">
              Patterns that indicate legitimate business operations. These reduce fraud risk assessment.
            </p>
          </div>
          <FlagItem
            label="Long Gaps Between Switches (>180 days)"
            detected={fraudAnalysis.phase3Whitelist.longGaps}
            tooltip={phase3Tooltips.longGaps}
            variant="green"
          />
          <FlagItem
            label="Legitimate Sale Pattern"
            detected={fraudAnalysis.phase3Whitelist.legitimateSale}
            tooltip={phase3Tooltips.legitimateSale}
            variant="green"
          />
          <FlagItem
            label="Linear Progression (No Returns)"
            detected={fraudAnalysis.phase3Whitelist.linearProgression}
            tooltip={phase3Tooltips.linearProgression}
            variant="green"
          />
          <FlagItem
            label="Rental Unit Identified"
            detected={fraudAnalysis.phase3Whitelist.rentalUnit}
            tooltip={phase3Tooltips.rentalUnit}
            variant="green"
          />
          <FlagItem
            label="Post-Revocation Dispersal"
            detected={fraudAnalysis.phase3Whitelist.postRevocationDispersal}
            tooltip={phase3Tooltips.postRevocationDispersal}
            variant="green"
          />
        </ExpandableSection>

        {/* Section 5: Decision Matrix */}
        <div className={`mt-4 p-4 rounded-lg border-2 ${
          fraudAnalysis.level === 'CRITICAL' ? 'border-red-500 bg-red-50' :
          fraudAnalysis.level === 'HIGH' ? 'border-orange-500 bg-orange-50' :
          fraudAnalysis.level === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
          'border-green-500 bg-green-50'
        }`}>
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Decision Matrix Result
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600">Condition:</span>
              <p className="font-medium text-gray-900 mt-1">{fraudAnalysis.decisionMatrix.condition}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Recommended Action:</span>
              <p className={`font-bold mt-1 ${
                fraudAnalysis.level === 'CRITICAL' || fraudAnalysis.level === 'HIGH' ? 'text-red-600' :
                fraudAnalysis.level === 'MEDIUM' ? 'text-yellow-700' : 'text-green-600'
              }`}>
                {fraudAnalysis.decisionMatrix.action}
              </p>
            </div>
          </div>
        </div>

        {/* Section 6: Detailed Evidence */}
        {fraudAnalysis.details && fraudAnalysis.details.length > 0 && (
          <ExpandableSection
            title="Detailed Evidence"
            badge={<Badge variant="info" size="sm">{fraudAnalysis.details.length} items</Badge>}
            defaultExpanded={fraudAnalysis.level === 'CRITICAL' || fraudAnalysis.level === 'HIGH'}
          >
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {fraudAnalysis.details.map((detail, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded text-sm ${
                    detail.toLowerCase().includes('critical') ? 'bg-red-50 text-red-800 border-l-4 border-red-500' :
                    detail.toLowerCase().includes('warning') || detail.toLowerCase().includes('high') ? 'bg-orange-50 text-orange-800 border-l-4 border-orange-500' :
                    detail.toLowerCase().includes('legitimate') || detail.toLowerCase().includes('normal') ? 'bg-green-50 text-green-800 border-l-4 border-green-500' :
                    'bg-gray-50 text-gray-800 border-l-4 border-gray-300'
                  }`}
                >
                  {detail}
                </div>
              ))}
            </div>
          </ExpandableSection>
        )}

        {/* Carrier Status Reference */}
        {fraudAnalysis.carrierStatuses && fraudAnalysis.carrierStatuses.length > 0 && (
          <ExpandableSection
            title="Carrier Statuses"
            badge={<Badge variant="info" size="sm">{fraudAnalysis.carrierStatuses.length} carriers</Badge>}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">DOT Number</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Carrier Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {fraudAnalysis.carrierStatuses.map((carrier, idx) => (
                    <tr key={idx} className="border-t border-gray-200">
                      <td className="px-3 py-2 font-mono text-gray-900">{carrier.dotNumber}</td>
                      <td className="px-3 py-2 text-gray-900">{carrier.carrierName}</td>
                      <td className="px-3 py-2">
                        <Badge
                          variant={
                            carrier.statusCode === 'A' ? 'success' :
                            carrier.statusCode === 'I' ? 'warning' :
                            carrier.statusCode === 'O' ? 'danger' : 'default'
                          }
                          size="sm"
                        >
                          {carrier.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ExpandableSection>
        )}

        {/* Export Button */}
        {onExport && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={onExport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Fraud Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FraudAnalysisBreakdown;
